import { motion } from "framer-motion";
import { ArrowUpRight, Copy, Check, Radio } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";
import { IMAGES, COIN } from "@/lib/constants";
import { Button } from "@/components/ui/button";

export default function Hero() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copyContract = async () => {
    try {
      await navigator.clipboard.writeText(COIN.contract);
      setCopied(true);
      toast.success(t("hero.copy_success_toast"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("hero.copy_fail_toast"));
    }
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden pt-24 pb-16">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <img src={IMAGES.heroBg} alt="deep space" className="h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        <div className="absolute inset-0 starfield opacity-40" />
        <div className="absolute inset-0 scanlines pointer-events-none" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[80vh]">
        {/* Left / Text */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-sm border border-primary/40 bg-primary/5 px-3 py-1.5"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            <span className="font-mono-alt text-[10px] tracking-[0.3em] text-primary">{t("hero.badge_prefix")} // {t("hero.badge_suffix")}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl"
          >
            {t("hero.title_l1")}
            <br />
            <span className="text-primary neon-glow-primary">{t("hero.title_l2")}</span>
            <br />
            <span className="stroke-text">{t("hero.title_l3")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 max-w-xl text-base md:text-lg text-muted-foreground"
          >
            {COIN.name} {t("hero.paragraph_1")} <span className="text-secondary">Solana</span>. {t("hero.paragraph_2")}
            {" "}<span className="text-primary">{t("hero.paragraph_tagline")}</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button
              asChild
              size="lg"
              data-testid="hero-cta-buy"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs px-7 py-6 shadow-[0_0_40px_-8px_hsl(var(--primary))]"
            >
              <a href="#how-to-buy">
                {t("hero.cta_buy")} <ArrowUpRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              data-testid="hero-cta-community"
              className="rounded-sm border-secondary/50 text-secondary hover:bg-secondary/10 hover:text-secondary font-mono-alt tracking-widest text-xs px-7 py-6"
            >
              <a href="#community">{t("hero.cta_community")}</a>
            </Button>
          </motion.div>

          {/* Contract */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-10 flex items-center gap-3 rounded-sm border border-primary/25 bg-black/40 px-4 py-3 max-w-xl"
          >
            <span className="font-mono-alt text-[10px] tracking-widest text-muted-foreground shrink-0">{t("hero.contract_label")}</span>
            <code data-testid="hero-contract-address" className="font-mono-alt text-xs md:text-sm text-primary truncate">
              {COIN.contract}
            </code>
            <button
              onClick={copyContract}
              data-testid="copy-contract-btn"
              className="ml-auto inline-flex items-center gap-1 rounded-sm border border-primary/40 hover:bg-primary/10 px-2 py-1 text-xs font-mono-alt text-primary transition-colors"
              aria-label="Copiar contrato"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t("hero.contract_copy_ok") : t("hero.contract_copy_btn")}
            </button>
          </motion.div>
        </div>

        {/* Right / Logo */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative aspect-square w-full max-w-md mx-auto"
          >
            <div className="absolute -inset-8 rounded-full bg-primary/25 blur-3xl" />
            <div className="absolute -inset-2 rounded-full bg-secondary/15 blur-2xl" />
            <div className="relative bob">
              <img
                src={COIN.logo}
                alt="UFO Coin logo"
                className="h-full w-full object-contain drop-shadow-[0_0_60px_rgba(26,168,255,0.5)]"
              />
            </div>
            {/* Rotating orbit ring */}
            <div className="absolute inset-0 rounded-full border border-primary/20 animate-[spin_40s_linear_infinite] pointer-events-none" />
            <div className="absolute inset-6 rounded-full border border-secondary/15 animate-[spin_60s_linear_infinite_reverse] pointer-events-none" />
          </motion.div>
        </div>
      </div>

      {/* Launch status ribbon */}
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 mt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          data-testid="launch-status-banner"
          className="relative overflow-hidden flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-sm border border-primary/30 bg-black/60 p-6"
        >
          <div className="flex items-start gap-4">
            <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-sm border border-primary/50 bg-primary/10 text-primary">
              <Radio className="h-5 w-5 animate-pulse" />
            </span>
            <div>
              <p className="font-mono-alt text-[10px] tracking-[0.3em] text-secondary">{t("hero.mission_status")}</p>
              <p className="font-display font-black text-xl md:text-2xl mt-1">
                {t("hero.launch_title_prefix")} <span className="text-primary neon-glow-primary">PUMP.FUN</span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{t("hero.launch_note")}</p>
            </div>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button
              asChild
              data-testid="launch-cta-pumpfun"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs px-5"
            >
              <a href={COIN.socials.pumpfun} target="_blank" rel="noreferrer">{t("hero.launch_cta_pump")}</a>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="launch-cta-telegram"
              className="rounded-sm border-secondary/50 text-secondary hover:bg-secondary/10 hover:text-secondary font-mono-alt tracking-widest text-xs px-5"
            >
              <a href={COIN.socials.telegram} target="_blank" rel="noreferrer">{t("hero.launch_cta_telegram")}</a>
            </Button>
          </div>
          <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        </motion.div>
      </div>
    </section>
  );
}
